import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClipboardList, Plus, Play, CheckCircle2, Eye, Trash2, Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { DropdownBadge, DropdownSelect } from "../components/shared/DropdownHooks";

export default function ProceduresPage({ onLogout, userRole, isFeatureEnabled }) {
  const [tab, setTab] = useState("procedures");
  const [procs, setProcs] = useState([]);
  const [execs, setExecs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [execDetail, setExecDetail] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "maintenance", steps: [{ title: "Step 1", description: "", type: "action", requires_witness: false, requires_evidence: false }] });
  const isAdmin = userRole === "admin";

  const fetchAll = useCallback(async () => {
    try {
      const [pR, eR, sR] = await Promise.allSettled([
        axios.get(`${API}/procedures`, { headers: getAuthHeaders() }),
        axios.get(`${API}/procedure-executions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/procedures/stats`, { headers: getAuthHeaders() }),
      ]);
      if (pR.status === "fulfilled") setProcs(pR.value.data || []);
      if (eR.status === "fulfilled") setExecs(eR.value.data || []);
      if (sR.status === "fulfilled") setStats(sR.value.data || {});
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { title: `Step ${f.steps.length + 1}`, description: "", type: "action", requires_witness: false, requires_evidence: false }] }));
  const removeStep = (i) => setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const updateStep = (i, field, val) => setForm(f => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (form.steps.length === 0) { toast.error("Add at least one step"); return; }
    try {
      await axios.post(`${API}/procedures`, form, { headers: getAuthHeaders() });
      toast.success("Procedure created");
      setCreateOpen(false);
      setForm({ title: "", description: "", category: "maintenance", steps: [{ title: "Step 1", description: "", type: "action", requires_witness: false, requires_evidence: false }] });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startExecution = async (pid) => {
    try {
      const resp = await axios.post(`${API}/procedures/${pid}/execute`, {}, { headers: getAuthHeaders() });
      toast.success("Execution started");
      setExecDetail(resp.data);
      setTab("executions");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const signoffStep = async (eid, stepIndex, notes) => {
    try {
      await axios.post(`${API}/procedure-executions/${eid}/signoff/${stepIndex}`, { evidence_notes: notes || "" }, { headers: getAuthHeaders() });
      toast.success(`Step ${stepIndex + 1} signed off`);
      const resp = await axios.get(`${API}/procedure-executions/${eid}`, { headers: getAuthHeaders() });
      setExecDetail(resp.data);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const witnessStep = async (eid, stepIndex) => {
    try {
      await axios.post(`${API}/procedure-executions/${eid}/witness/${stepIndex}`, {}, { headers: getAuthHeaders() });
      toast.success(`Step ${stepIndex + 1} witnessed`);
      const resp = await axios.get(`${API}/procedure-executions/${eid}`, { headers: getAuthHeaders() });
      setExecDetail(resp.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteProc = async (id) => {
    if (!window.confirm("Delete this procedure?")) return;
    try { await axios.delete(`${API}/procedures/${id}`, { headers: getAuthHeaders() }); toast.success("Deleted"); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="procedures-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PageHeader title="Electronic Procedures" subtitle="Step-by-step procedures with signoffs and evidence capture" testId="procedures-title" />
          <Button onClick={() => setCreateOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none text-xs font-bold" data-testid="create-proc-btn"><Plus size={14} className="mr-1" /> New Procedure</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["Procedures", stats.total_procedures, "#0F172A"], ["Active", stats.active_procedures, "#10B981"], ["Executions", stats.total_executions, "#2563EB"], ["In Progress", stats.in_progress, "#D97706"], ["Completed", stats.completed, "#10B981"]].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{l}</p><p className="text-2xl font-heading font-black mt-0.5" style={{ color: c }}>{v || 0}</p></div>
          ))}
        </div>
        <div className="flex gap-1">
          {[["procedures", `Procedures (${procs.length})`], ["executions", `Executions (${execs.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-5 py-2.5 text-xs font-bold tracking-wider border ${tab === k ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#64748B] border-[#E2E8F0]"}`} data-testid={`tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "procedures" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="proc-list">
            {procs.length === 0 ? <div className="p-12 text-center text-sm text-[#94A3B8]"><ClipboardList size={36} className="mx-auto mb-2 text-[#CBD5E1]" />No procedures yet.</div> : (
              <div className="divide-y divide-[#F1F5F9]">
                {procs.map(p => (
                  <div key={p.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`proc-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><span className="font-mono text-xs text-[#2563EB] font-bold">{p.proc_number}</span><DropdownBadge dropdownKey="procedure_statuses" value={p.status} /></div>
                      <p className="text-sm font-medium text-[#0F172A] mt-1">{p.title}</p>
                      <p className="text-[11px] text-[#64748B]">{p.step_count} steps · Rev {p.revision} · By {p.created_by_name}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" onClick={() => startExecution(p.id)} className="rounded-none bg-[#10B981] text-white text-[10px] font-bold" data-testid={`execute-${p.id}`}><Play size={11} className="mr-1" />Execute</Button>
                      {isAdmin && <Button size="sm" variant="outline" onClick={() => deleteProc(p.id)} className="rounded-none border-[#FECACA] text-[#DC2626] text-[10px]"><Trash2 size={11} /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "executions" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="exec-list">
            {execs.length === 0 ? <div className="p-12 text-center text-sm text-[#94A3B8]">No executions yet.</div> : (
              <div className="divide-y divide-[#F1F5F9]">
                {execs.map(e => {
                  const completed = (e.step_states || []).filter(s => s.status === "completed").length;
                  return (
                    <div key={e.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA] cursor-pointer" onClick={() => setExecDetail(e)} data-testid={`exec-${e.id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-[#7C3AED] font-bold">{e.proc_number}</span>
                          <span className={`text-[10px] px-2 py-0.5 font-bold uppercase ${e.status === "completed" ? "text-[#10B981] bg-[#F0FDF4] border border-[#BBF7D0]" : "text-[#D97706] bg-[#FFFBEB] border border-[#FDE68A]"}`}>{e.status}</span>
                        </div>
                        <p className="text-sm text-[#0F172A] mt-1">{e.proc_title}</p>
                        <p className="text-[11px] text-[#64748B]">{completed}/{e.total_steps} steps · By {e.started_by_name} · {new Date(e.started_at).toLocaleString()}</p>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-none text-[10px]"><Eye size={11} /></Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Procedure Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-proc-dialog">
          <DialogHeader><DialogTitle>New Electronic Procedure</DialogTitle><DialogDescription className="text-xs text-[#64748B]">Build a step-by-step procedure with signoff requirements.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="proc-title" /></div>
              <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Category</Label><Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="proc-category" /></div>
            </div>
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Description</Label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm h-14 resize-none" data-testid="proc-desc" /></div>
            <div className="border-t border-[#E2E8F0] pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[10px] font-bold uppercase text-[#64748B]">Steps ({form.steps.length})</Label>
                <Button size="sm" onClick={addStep} variant="outline" className="rounded-none text-[10px]" data-testid="add-step-btn"><Plus size={11} className="mr-1" />Add Step</Button>
              </div>
              <div className="space-y-2">
                {form.steps.map((s, i) => (
                  <div key={i} className="border border-[#E2E8F0] p-2.5 bg-[#FAFAFA]" data-testid={`step-${i}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-bold text-[#94A3B8] w-6">{i + 1}.</span>
                      <Input value={s.title} onChange={e => updateStep(i, "title", e.target.value)} className="rounded-none text-xs h-7 flex-1" placeholder="Step title" />
                      <DropdownSelect dropdownKey="step_types" value={s.type} onChange={e => updateStep(i, "type", e.target.value)} className="border border-[#E2E8F0] px-2 py-1 text-[10px] w-28" testId={`step-type-${i}`} />
                      <label className="flex items-center gap-1 text-[10px] text-[#64748B]"><input type="checkbox" checked={s.requires_witness} onChange={e => updateStep(i, "requires_witness", e.target.checked)} />Witness</label>
                      <label className="flex items-center gap-1 text-[10px] text-[#64748B]"><input type="checkbox" checked={s.requires_evidence} onChange={e => updateStep(i, "requires_evidence", e.target.checked)} />Evidence</label>
                      {form.steps.length > 1 && <button onClick={() => removeStep(i)} className="text-[#DC2626] hover:text-[#991B1B]"><Trash2 size={12} /></button>}
                    </div>
                    <Input value={s.description} onChange={e => updateStep(i, "description", e.target.value)} className="rounded-none text-[10px] h-6" placeholder="Step description (optional)" />
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={handleCreate} className="w-full bg-[#2563EB] text-white rounded-none text-xs font-bold" data-testid="proc-submit-btn">Create Procedure</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Execution Detail Dialog */}
      <Dialog open={!!execDetail} onOpenChange={(o) => { if (!o) setExecDetail(null); }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="exec-detail-dialog">
          <DialogHeader>
            <DialogTitle>{execDetail?.proc_number} — Execution</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">{execDetail?.proc_title} · {execDetail?.status === "completed" ? "Completed" : `Step ${(execDetail?.current_step || 0) + 1} of ${execDetail?.total_steps}`}</DialogDescription>
          </DialogHeader>
          {execDetail && (
            <div className="space-y-2 mt-2">
              {(execDetail.step_states || []).map((s, i) => {
                const isCurrent = i === execDetail.current_step && execDetail.status === "in_progress";
                const done = s.status === "completed";
                return (
                  <div key={i} className={`border p-3 ${isCurrent ? "border-[#2563EB] bg-[#EFF6FF]" : done ? "border-[#BBF7D0] bg-[#F0FDF4]" : "border-[#E2E8F0]"}`} data-testid={`exec-step-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 flex items-center justify-center text-[10px] font-bold ${done ? "bg-[#10B981] text-white" : isCurrent ? "bg-[#2563EB] text-white" : "bg-[#E2E8F0] text-[#64748B]"}`}>{i + 1}</span>
                      <span className="text-sm font-medium text-[#0F172A] flex-1">{s.title}</span>
                      <DropdownBadge dropdownKey="step_types" value={s.type} />
                      {done && <CheckCircle2 size={14} className="text-[#10B981]" />}
                    </div>
                    {done && <p className="text-[10px] text-[#64748B] mt-1 ml-8">Signed off by {s.signed_off_by_name} · {new Date(s.signed_off_at).toLocaleString()}{s.evidence_notes ? ` · Note: ${s.evidence_notes}` : ""}</p>}
                    {s.requires_witness && s.witness_by && <p className="text-[10px] text-[#7C3AED] mt-0.5 ml-8">Witnessed by {s.witness_by_name} · {new Date(s.witness_at).toLocaleString()}</p>}
                    {isCurrent && (
                      <div className="flex gap-2 mt-2 ml-8">
                        <Button size="sm" onClick={() => signoffStep(execDetail.id, i, "")} className="rounded-none bg-[#10B981] text-white text-[10px] font-bold" data-testid={`signoff-${i}`}><CheckCircle2 size={11} className="mr-1" />Sign Off</Button>
                        {s.requires_witness && !s.witness_by && <Button size="sm" variant="outline" onClick={() => witnessStep(execDetail.id, i)} className="rounded-none text-[10px] font-bold border-[#7C3AED] text-[#7C3AED]" data-testid={`witness-${i}`}><UserCheck size={11} className="mr-1" />Witness</Button>}
                      </div>
                    )}
                  </div>
                );
              })}
              {execDetail.status === "completed" && <div className="p-3 bg-[#F0FDF4] border border-[#BBF7D0] text-center text-sm text-[#10B981] font-bold">Procedure completed at {new Date(execDetail.completed_at).toLocaleString()}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
